import { useForm, yupResolver } from "@mantine/form";
import {
  CreateVariantMetricInput,
  useCreateVariantMetricMutation,
  useDeleteVariantMetricMutation,
  useUpdateVariantMetricMutation,
} from "src/generated/graphql";
import { memo, useEffect } from "react";
import { useModals } from "@mantine/modals";
import FormCard from "src/components/FormCard";
import { Button, Group, useMantineTheme } from "@mantine/core";
import { GetProductGroup } from "src/api/graphql/queries/Product";
import { array, object, string } from "yup";
import TextInput from "src/components/TextInput/TextInput";
import { errorNotification } from "src/utils/alerts";
import VarientMetricOption from "../VarientMetricOption";
import { IvariantMetrics } from "src/api/context/ProductContext";
import useWritePermission from "src/hooks/usePermission";
import { EMPLOYEE_ROLE_OBJ } from "src/utils/constants";

export type IVariantMetricForm = Omit<
  CreateVariantMetricInput,
  "productGroup"
> & {
  productGroup?: string;
};

const VariantMetricFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Metric Name should must be least 2 letters")
    .max(30, "Metric Name must not be greater than 30 letters")
    .required("Metric Name is required"),
  values: array().of(
    object().shape({
      name: string()
        .trim("No spaces allowed")
        .strict(true)
        .min(2, "Name should must be least 2 letters")
        .max(30, "Name must not be greater than 30 letters")
        .required("Name is required"),
    })
  ),
});

interface VariantMetricDetailProps {
  variant: NonNullable<IvariantMetrics>[0];
  variantArr: IvariantMetrics;
  handleModal?: (variant: NonNullable<IvariantMetrics>[0]) => void;
  isModal?: boolean;
  groupId?: string;
  isAllowWrite: boolean;
}

const VariantMetricDetail = ({
  variant,
  variantArr,
  isModal,
  handleModal,
  groupId,
  isAllowWrite,
}: VariantMetricDetailProps) => {
  const modals = useModals();
  const theme = useMantineTheme();
  const refetchQuery = [
    {
      query: GetProductGroup,
      variables: { productGroupOneId: groupId },
    },
  ];

  useEffect(() => {
    if (variant && isModal) {
      let _obj = {
        name: variant.name,
        values: variant.values!,
      };
      form.setValues(_obj);
      form.resetDirty(_obj);
    }
  }, [variant, isModal]);

  const [createVariant, { loading: createLoading }] =
    useCreateVariantMetricMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [updateVariant, { loading: updateLoading }] =
    useUpdateVariantMetricMutation({
      onCompleted: () => {
        modals.closeAll();
      },
      refetchQueries: refetchQuery,
    });

  const [deleteVariantMetric, { loading: deleteLoading }] =
    useDeleteVariantMetricMutation({
      refetchQueries: refetchQuery,
    });

  const form = useForm<IVariantMetricForm>({
    initialValues: {
      name: "",
      values: [{ name: "" }],
    },
    validate: yupResolver(VariantMetricFormSchema),
  });

  const handleDelete = () => {
    variant &&
      deleteVariantMetric({
        variables: { id: variant.id },
      });
  };

  const handleSubmit = async (detail: IVariantMetricForm) => {
    // check for array includes metric name case insensitive
    let _variantArr = variant?.id
      ? variantArr?.filter((e) => e.id !== variant.id)
      : variantArr;
    if (
      _variantArr?.some(
        (x) => x.name.toLowerCase() == detail.name.toLowerCase()
      )
    ) {
      return errorNotification({
        message: "Metric name already exist",
      });
    }

    variant?.id
      ? updateVariant({
          variables: { data: { ...detail }, id: variant.id },
        })
      : createVariant({
          variables: { data: { ...detail, productGroup: { id: groupId! } } },
        });
  };

  const handleEdit = () => {
    handleModal && handleModal(variant);
  };

  return (
    <>
      <FormCard
        title={variant?.name}
        loading={deleteLoading || updateLoading}
        onDelete={isAllowWrite ? handleDelete : undefined}
        onEdit={isAllowWrite ? handleEdit : undefined}
        onSave={form.onSubmit((values) => handleSubmit(values))}
        hideCard={isModal}
        mb={10}
        size="md"
        radius={4}
      >
        {isModal && (
          <>
            <TextInput
              withAsterisk
              label="Metric Name eg. Size"
              {...form.getInputProps("name")}
            />

            <VarientMetricOption form={form} />

            <Group position="apart" mt="md" style={{ marginTop: 30 }}>
              <Button
                radius="md"
                variant="outline"
                onClick={() => modals.closeAll()}
                style={{
                  flex: 1,
                  color: theme.colors.secondaryTextColor[6],
                }}
                sx={{
                  backgroundColor: theme.colors.secondary[6],
                  "&:hover": {
                    background: theme.colors.secondary[5],
                  },
                }}
              >
                Cancel
              </Button>
              <Button
                radius="md"
                type="submit"
                disabled={createLoading || updateLoading}
                style={{
                  flex: 1,
                }}
                sx={{
                  color: theme.colors.primaryTextColor[6],
                  "&:hover": {
                    backgroundColor: theme.colors.primary[4],
                  },
                }}
                loading={createLoading || updateLoading}
              >
                {variant ? "Save" : "Add"}
              </Button>
            </Group>
          </>
        )}
      </FormCard>
    </>
  );
};

export default memo(VariantMetricDetail);
